import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Form,
  FormFeedback,
  Button,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import TableContainer from "components/Common/TableContainer";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { useFormik } from "formik";
import moment from "moment";

import {
  getInquires,
  createInquire,
  updateInquire,
} from "api";
import { handleError } from "utils/errorHandler";

const INQUIRE_TYPE_OPTIONS = [
  { value: "venue", label: "Venue" },
  { value: "artist", label: "Artist" },
  { value: "other", label: "Other" },
];

const TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "venue", label: "Venue" },
  { value: "artist", label: "Artist" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "responded", label: "Responded" },
  { value: "closed", label: "Closed" },
];

const InquireList = () => {
  document.title = "Inquire | EventoX";

  const [inquires, setInquires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState("");

  useEffect(() => {
    try {
      const auth = localStorage.getItem("authUser");
      if (auth) {
        const parsed = JSON.parse(auth);
        const id = parsed?.user?.userId ?? parsed?.userId;
        if (id != null) setCurrentUserId(String(id));
      }
    } catch (_) {}
  }, []);

  const toggle = () => setModal(!modal);

  const filteredInquires = useMemo(() => {
    if (!Array.isArray(inquires)) return [];
    if (typeFilter === "all") return inquires;
    return inquires.filter((i) => (i.inquireType || i.type) === typeFilter);
  }, [inquires, typeFilter]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      inquireType: selectedItem?.inquireType ?? selectedItem?.type ?? "venue",
      requestUserId: selectedItem?.requestUserId ?? currentUserId ?? "",
      targetUserId: selectedItem?.targetUserId ?? "",
      purpose: selectedItem?.purpose ?? "",
      eventDate: selectedItem?.eventDate ?? "",
      location: selectedItem?.location ?? "",
      budget: selectedItem?.budget ?? "",
      expectedGuest: selectedItem?.expectedGuest ?? "",
      additionalMsg: selectedItem?.additionalMsg ?? "",
      status: selectedItem?.status ?? "pending",
      responseMsg: selectedItem?.responseMsg ?? "",
    },
    validationSchema: Yup.object({
      inquireType: Yup.string()
        .oneOf(["venue", "artist", "other"])
        .required("Type is required"),
      requestUserId: Yup.string().required("Request user is required"),
    }),
    onSubmit: async (values) => {
      setSubmitLoading(true);
      try {
        const payload = {
          inquireType: values.inquireType,
          requestUserId: Number(values.requestUserId),
          targetUserId: values.targetUserId ? Number(values.targetUserId) : undefined,
          purpose: values.purpose || undefined,
          eventDate: values.eventDate || undefined,
          location: values.location || undefined,
          budget: values.budget || undefined,
          expectedGuest: values.expectedGuest ? Number(values.expectedGuest) : undefined,
          additionalMsg: values.additionalMsg || undefined,
          status: values.status || undefined,
        };
        if (isEdit) {
          payload.responseMsg = values.responseMsg || undefined;
        }

        const id = selectedItem?.id ?? selectedItem?.inquireId;
        if (isEdit && id) {
          await updateInquire(id, payload);
          toast.success("Inquire updated");
        } else {
          await createInquire(payload);
          toast.success("Inquire created");
        }
        const data = await getInquires();
        setInquires(Array.isArray(data) ? data : []);
        validation.resetForm();
        setSelectedItem(null);
        setIsEdit(false);
        toggle();
      } catch (err) {
        handleError(err);
        toast.error("Error saving inquire");
      } finally {
        setSubmitLoading(false);
      }
    },
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getInquires();
        setInquires(Array.isArray(data) ? data : []);
      } catch (err) {
        handleError(err);
        toast.error("Failed to load inquires");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsEdit(true);
    validation.resetForm();
    toggle();
  };

  const handleAddNew = () => {
    setSelectedItem(null);
    setIsEdit(false);
    validation.setFieldValue("requestUserId", currentUserId || "");
    validation.resetForm();
    toggle();
  };

  const formatDate = (d) => (d && moment(d).isValid() ? moment(d).format("MMM D, YYYY") : "—");

  const columns = useMemo(
    () => [
      {
        header: "Type",
        accessorKey: "inquireType",
        cell: (cell) => {
          const t = cell.getValue() || cell.row.original?.type;
          return (
            <span className={`badge ${t === "venue" ? "bg-info" : t === "artist" ? "bg-success" : "bg-secondary"}`}>
              {t || "—"}
            </span>
          );
        },
      },
      {
        header: "Request User",
        accessorKey: "requestUser",
        cell: (c) => {
          const u = c.getValue() || c.row.original?.requestUser;
          if (!u) return c.row.original?.requestUserId ?? "—";
          return (
            <div className="text-break">
              <div className="fw-medium">{u.name || u.username || "—"}</div>
              {u.email && <div className="small text-muted">{u.email}</div>}
            </div>
          );
        },
      },
      {
        header: "Target User",
        accessorKey: "targetUser",
        cell: (c) => {
          const u = c.getValue() || c.row.original?.targetUser;
          if (!u) return "—";
          return (
            <div className="text-break">
              <div className="fw-medium">{u.name || u.username || "—"}</div>
              {u.email && <div className="small text-muted">{u.email}</div>}
            </div>
          );
        },
      },
      { header: "Purpose", accessorKey: "purpose", cell: (c) => (c.getValue() || "").slice(0, 30) + (c.getValue() && c.getValue().length > 30 ? "…" : "") },
      { header: "Event Date", accessorKey: "eventDate", cell: (c) => formatDate(c.getValue()) },
      { header: "Location", accessorKey: "location", cell: (c) => c.getValue() ?? "—" },
      { header: "Budget", accessorKey: "budget", cell: (c) => c.getValue() ?? "—" },
      { header: "Guests", accessorKey: "expectedGuest", cell: (c) => c.getValue() ?? "—" },
      {
        header: "Status",
        accessorKey: "status",
        cell: (c) => {
          const s = c.getValue();
          const cls = s === "responded" ? "bg-success" : s === "closed" ? "bg-secondary" : "bg-warning";
          return <span className={`badge ${cls}`}>{s || "pending"}</span>;
        },
      },
      {
        header:"Created At",
        accessorKey: "createdAt",
        cell: (c) => formatDate(c.getValue()),
      },
      {
        header: "Action",
        cell: (cellProps) => {
          const item = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="card-drop">
                <i className="mdi mdi-dots-horizontal font-size-18" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem onClick={() => handleEdit(item)}>Edit</DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Admin" breadcrumbItem="Inquire" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                  <Label className="mb-0 me-2">Type:</Label>
                  <Input
                    type="select"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    style={{ width: "auto", minWidth: 120 }}
                  >
                    {TYPE_FILTER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner />
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={filteredInquires}
                    isGlobalFilter
                    isPagination
                    SearchPlaceholder="Search inquires..."
                    isAddButton
                    handleUserClick={handleAddNew}
                    buttonClass="btn btn-success btn-rounded mb-2"
                    buttonName="Add Inquire"
                    tableClass="align-middle table-hover w-100"
                    theadClass="table-light"
                    paginationWrapper="dataTables_paginate pagination-rounded"
                    pagination="pagination"
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modal} toggle={toggle} size="lg" scrollable>
          <ModalHeader toggle={toggle}>
            {isEdit ? "Edit Inquire" : "Add Inquire"}
          </ModalHeader>
          <ModalBody>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
              }}
            >
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Type *</Label>
                    <Input
                      name="inquireType"
                      type="select"
                      onChange={validation.handleChange}
                      value={validation.values.inquireType}
                      invalid={validation.touched.inquireType && validation.errors.inquireType}
                    >
                      {INQUIRE_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </Input>
                    <FormFeedback>{validation.errors.inquireType}</FormFeedback>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Request User ID *</Label>
                    <Input
                      name="requestUserId"
                      type="number"
                      min={1}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.requestUserId}
                      invalid={validation.touched.requestUserId && validation.errors.requestUserId}
                    />
                    <FormFeedback>{validation.errors.requestUserId}</FormFeedback>
                  </div>
                </Col>
              </Row>

              <div className="mb-3">
                <Label>Target User ID (optional)</Label>
                <Input
                  name="targetUserId"
                  type="number"
                  min={0}
                  onChange={validation.handleChange}
                  value={validation.values.targetUserId}
                />
              </div>

              <div className="mb-3">
                <Label>Purpose</Label>
                <Input
                  name="purpose"
                  type="text"
                  onChange={validation.handleChange}
                  value={validation.values.purpose}
                  placeholder="e.g. Private event booking"
                />
              </div>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Event Date</Label>
                    <Input
                      name="eventDate"
                      type="date"
                      onChange={validation.handleChange}
                      value={validation.values.eventDate}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Location</Label>
                    <Input
                      name="location"
                      type="text"
                      onChange={validation.handleChange}
                      value={validation.values.location}
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Budget</Label>
                    <Input
                      name="budget"
                      type="text"
                      onChange={validation.handleChange}
                      value={validation.values.budget}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>Expected Guests</Label>
                    <Input
                      name="expectedGuest"
                      type="number"
                      min={0}
                      onChange={validation.handleChange}
                      value={validation.values.expectedGuest}
                    />
                  </div>
                </Col>
              </Row>

              <div className="mb-3">
                <Label>Additional Message</Label>
                <Input
                  name="additionalMsg"
                  type="textarea"
                  rows={2}
                  onChange={validation.handleChange}
                  value={validation.values.additionalMsg}
                />
              </div>

              {isEdit && (
                <>
                  <div className="mb-3">
                    <Label>Status</Label>
                    <Input
                      name="status"
                      type="select"
                      onChange={validation.handleChange}
                      value={validation.values.status}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </Input>
                  </div>
                  <div className="mb-3">
                    <Label>Response Message</Label>
                    <Input
                      name="responseMsg"
                      type="textarea"
                      rows={2}
                      onChange={validation.handleChange}
                      value={validation.values.responseMsg}
                      placeholder="Reply to the inquire"
                    />
                  </div>
                </>
              )}

              <div className="text-end">
                <Button color="secondary" className="me-2" onClick={toggle}>
                  Cancel
                </Button>
                <Button color="success" type="submit" disabled={submitLoading}>
                  {submitLoading ? <><Spinner size="sm" className="me-1" /> Saving...</> : "Save"}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default withRouter(InquireList);
